"use client";

import { LoaderCircle } from "lucide-react";
import { useTranslations } from "next-intl";
import { LottiePlayer } from "@/components/LottiePlayer";

type ResumePersonalizationTriggerProps = {
  isPersonalized: boolean;
  isUploading: boolean;
  onClick: () => void;
  className?: string;
};

function ResumePersonalizationLottie({
  isPersonalized,
}: {
  isPersonalized: boolean;
}) {
  return (
    <span
      aria-hidden
      data-resume-animation
      data-muted={isPersonalized ? "true" : "false"}
      className="block h-full w-full opacity-100 transition-[filter] duration-500"
      style={{
        filter: isPersonalized
          ? "grayscale(1) brightness(1.15) contrast(0.65) opacity(0.8)"
          : "none",
      }}
    >
      <LottiePlayer
        src="/animations/resume-personalization.json"
        autoplay
        loop
        minDevicePixelRatio={3}
        className="pointer-events-none h-full w-full"
      />
    </span>
  );
}

export function ResumePersonalizationTrigger({
  isPersonalized,
  isUploading,
  onClick,
  className = "",
}: ResumePersonalizationTriggerProps) {
  const t = useTranslations("profile.resumePersonalization");
  const label = isPersonalized ? t("trigger.manage") : t("trigger.personalize");

  return (
    <button
      type="button"
      aria-label={label}
      data-testid="resume-personalization-trigger"
      data-personalized={isPersonalized ? "true" : "false"}
      disabled={isUploading}
      onClick={onClick}
      title={label}
      className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-black bg-white p-1.5 outline-none transition-transform active:translate-y-0.5 focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 disabled:cursor-wait ${className}`}
    >
      <ResumePersonalizationLottie isPersonalized={isPersonalized} />
      {isUploading ? (
        <span className="absolute inset-1 flex items-center justify-center rounded-full bg-white/85">
          <LoaderCircle
            className="h-5 w-5 animate-spin text-black"
            aria-hidden
          />
        </span>
      ) : null}
    </button>
  );
}
